// Mappers for Monthly Attendance and Monthly Report
import type {
  LC_MonthlyAttendanceRecordOut,
  MonthlyAttendanceRecord,
  LC_GetMonthlyAttendanceOut,
  MonthlyAttendancePage,
  LC_GetMonthlyReportOut,
  MonthlyReportPage,
  LC_MonthlyReportDay,
  MonthlyReportDay,
} from '../types';
import { formatToDdMmYyyy } from '@/utils';


export function mapMonthlyAttendanceRecordOut(
  record: LC_MonthlyAttendanceRecordOut,
): MonthlyAttendanceRecord {
  return {
    id: String(record.id),
    fecha: record.date,
    dia: record.day,
    entrada: record.checkIn,
    salida: record.checkOut,
    horasPactadas: record.agreedHours,
    horasTrabajadas: record.workedHours,
    horasExtras: record.overtimeHours,
    horasAusencias: record.absenceHours,
    tieneDetalles: record.hasDetails,
    tipoHorasExtras: record.overtimeType ?? null,
    observacion: record.observation ?? null,
  };
}

export function mapMonthlyAttendancePage(
  response: LC_GetMonthlyAttendanceOut,
): MonthlyAttendancePage {
  const records = (response.data ?? []).map(mapMonthlyAttendanceRecordOut);
  const pagination = response.pagination;

  return {
    data: records,
    employeeName: response.employeeName,
    employeeId: response.employeeId,
    page: pagination.currentPage,
    pageSize: pagination.pageSize,
    totalRecords: pagination.totalRecords,
    totalPages: pagination.totalPages,
  };
}

/**
 * Determina los valores de entrada y salida basado en las marcas y horas pactadas
 * @param markingValues - Array de marcas registradas
 * @param hasAgreedHours - Indica si el día tiene horas pactadas
 * @returns Objeto con checkIn y checkOut calculados
 */
function determineCheckInOut(
  markingValues: string[],
  hasAgreedHours: boolean,
): { checkIn: string; checkOut: string } {
  let checkIn = '';
  let checkOut = '';
  
  if (markingValues.length === 0) {
    if (hasAgreedHours) {
      checkIn = 'Sin marca';
      checkOut = 'Sin marca';
    }
  } else if (markingValues.length === 1) {
    checkIn = markingValues[0];
    checkOut = hasAgreedHours ? 'Sin marca' : '';
  } else {
    checkIn = markingValues[0];
    checkOut = markingValues[markingValues.length - 1];
  }
  
  return { checkIn, checkOut };
}

export function mapMonthlyReportDay(
  transport: LC_MonthlyReportDay,
): MonthlyReportDay {
  let dayName = transport.dayOfWeek;
  try {
    let dateObj: Date;
    // Check for YYYY-MM-DD format to avoid timezone shifts (treat as local date)
    const isoDateMatch = String(transport.date).match(/^(\d{4})-(\d{2})-(\d{2})$/);

    if (isoDateMatch) {
      const [, year, month, day] = isoDateMatch;
      // Construct date at noon local time to be safe from DST/timezone shifts
      dateObj = new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        12,
        0,
        0,
      );
    } else {
      dateObj = new Date(transport.date);
    }

    if (!isNaN(dateObj.getTime())) {
      const formatter = new Intl.DateTimeFormat('es-CL', { weekday: 'long' });
      const spanishDay = formatter.format(dateObj);
      dayName = spanishDay.charAt(0).toUpperCase() + spanishDay.slice(1);
    }
  } catch {
    // Fallback to original if parsing fails
  }

  // Extract entry (first mark) and exit (last mark) from markings
  const markingValues = Object.values(transport.markings || {}).filter(Boolean).map(String);
  const agreedHoursTime = transport.hours.agreedHours_TIME;
  const hasAgreedHours = Boolean(agreedHoursTime && agreedHoursTime !== '00:00:00' && agreedHoursTime !== '00:00');
  
  const { checkIn, checkOut } = determineCheckInOut(markingValues, hasAgreedHours);

  return {
    id: transport.date,
    date: formatToDdMmYyyy(transport.date),
    dayName: dayName,
    checkIn: checkIn,
    checkOut: checkOut,
    marks: markingValues,
    agreedHours: transport.hours.agreedHours_TIME,
    workedHours: transport.hours.workedHours_TIME,
    overtime: transport.hours.totalOvertime_TIME,
    absences: transport.hours.totalAbsences_TIME,
    comments: transport.observations,
    isAbsent: transport.indicators.absentDay > 0,
    raw: transport,
  };
}

export function mapMonthlyReportPage(
  transport: LC_GetMonthlyReportOut,
): MonthlyReportPage {
  const { data } = transport;
  return {
    header: data.header,
    days: (data.days || []).map(mapMonthlyReportDay),
    weeks: data.weeks,
    periodTotals: data.periodTotals,
    pagination: {
      page: data.pagination.currentPage,
      pageSize: data.pagination.pageSize,
      totalRecords: data.pagination.totalRecords,
      totalPages: data.pagination.totalPages,
    },
  };
}
